import { z } from 'zod';
import type { DetailValidation } from './erreurs.js';
import { normaliserTelephone } from './telephone.js';

/**
 * Paramètres réglés par le super admin (page « Paramètres », PUT /parametres/:cle).
 * Chaque clé modifiable a son schéma, appliqué par l'API ET par l'écran : une
 * valeur hors bornes n'est jamais enregistrée. Les clés sans schéma — comme
 * `retention.candidats.action`, que rien ne lit — ne se modifient pas.
 */

export const CLE_TELEPHONES_EXCLUS = 'cv.telephones_exclus';

export const CLES_REGLAGES_ENTIERS = [
  'retention.candidats.mois',
  'retention.candidats.preavis.jours',
  'retention.employes.mois',
  'justificatifs.delai.jours',
  'vivier.note.minimum',
  'retention.cv_a_rattacher.jours',
  'formulaire.max_par_ip_heure',
  'formulaire.max_par_ip_jour',
  'formulaire.duree_min_saisie_s',
  'formulaire.taille_max_cv_mo',
  'formulaire.purge_rejets_jours',
] as const;
export type CleReglageEntier = (typeof CLES_REGLAGES_ENTIERS)[number];

export interface ReglageEntier {
  cle: CleReglageEntier;
  libelle: string;
  unite: string;
  min: number;
  max: number;
  /** Valeur appliquée quand le paramètre est absent ou invalide en base. */
  defaut: number;
  aide: string | null;
}

/**
 * Bornes : l'API et le worker lisent ces entiers par `parametreEntier` /
 * `parametre_entier`, qui ignorent tout ce qui n'est pas un entier strictement
 * positif — d'où un minimum de 1, y compris pour la note du vivier (tranché le
 * 2026-09-15 : de 1 à 5, sans 0 « aucun seuil » ; le filtre de qualité se
 * désactive dans l'écran du vivier).
 */
export const REGLAGES_ENTIERS: Record<CleReglageEntier, ReglageEntier> = {
  'retention.candidats.mois': {
    cle: 'retention.candidats.mois',
    libelle: 'Conservation des candidats',
    unite: 'mois',
    min: 1,
    max: 120,
    defaut: 24,
    aide: 'Après la dernière candidature ou le dernier contact effectif ; anonymisation ensuite',
  },
  'retention.candidats.preavis.jours': {
    cle: 'retention.candidats.preavis.jours',
    libelle: 'Préavis avant anonymisation',
    unite: 'jours',
    min: 1,
    max: 365,
    defaut: 30,
    aide: null,
  },
  'retention.employes.mois': {
    cle: 'retention.employes.mois',
    libelle: 'Conservation des salariés',
    unite: 'mois',
    min: 1,
    max: 600,
    defaut: 60,
    aide: 'Non appliqué tant qu’un juriste ne l’a pas fixé',
  },
  'justificatifs.delai.jours': {
    cle: 'justificatifs.delai.jours',
    libelle: 'Délai de remise des justificatifs',
    unite: 'jours',
    min: 1,
    max: 90,
    defaut: 7,
    aide: 'Compté depuis la première charte signée',
  },
  'vivier.note.minimum': {
    cle: 'vivier.note.minimum',
    libelle: 'Note minimale du filtre qualité du vivier',
    unite: 'sur 5',
    min: 1,
    max: 5,
    defaut: 3,
    aide: 'Note d’entretien ou pastille de potentiel, de 1 à 5 ; pour voir tous les anciens candidats, décochez le filtre de qualité dans l’écran du vivier',
  },
  'retention.cv_a_rattacher.jours': {
    cle: 'retention.cv_a_rattacher.jours',
    libelle: 'Effacement des CV non rattachés',
    unite: 'jours',
    min: 1,
    max: 365,
    defaut: 30,
    aide: 'Après leur dépôt, sans préavis ; le CV reste téléchargeable sur la plateforme',
  },
  // Formulaire public (docs/FORMULAIRE_PUBLIC.md § 3). Les bornes de débit sont
  // volontairement basses : une famille postule depuis la même connexion, mais
  // pas quarante fois dans l'heure.
  'formulaire.max_par_ip_heure': {
    cle: 'formulaire.max_par_ip_heure',
    libelle: 'Envois du formulaire par heure',
    unite: 'par adresse',
    min: 1,
    max: 100,
    defaut: 5,
    aide: 'Au-delà, l’envoi est refusé sans que le candidat l’apprenne',
  },
  'formulaire.max_par_ip_jour': {
    cle: 'formulaire.max_par_ip_jour',
    libelle: 'Envois du formulaire par jour',
    unite: 'par adresse',
    min: 1,
    max: 500,
    defaut: 20,
    aide: null,
  },
  'formulaire.duree_min_saisie_s': {
    cle: 'formulaire.duree_min_saisie_s',
    libelle: 'Durée minimale de saisie',
    unite: 'secondes',
    min: 1,
    max: 60,
    defaut: 4,
    aide: 'Un formulaire rempli plus vite qu’un humain est traité comme un robot',
  },
  'formulaire.taille_max_cv_mo': {
    cle: 'formulaire.taille_max_cv_mo',
    libelle: 'Taille maximale d’un CV déposé au formulaire',
    unite: 'Mo',
    min: 1,
    max: 20,
    defaut: 5,
    aide: 'La limite du frontal doit rester au-dessus de cette valeur',
  },
  'formulaire.purge_rejets_jours': {
    cle: 'formulaire.purge_rejets_jours',
    libelle: 'Effacement des envois rejetés',
    unite: 'jours',
    min: 1,
    max: 365,
    defaut: 30,
    aide: 'Robots et dépassements de débit ; les envois reçus suivent la personne',
  },
};

export const CLES_PARAMETRES_MODIFIABLES = [CLE_TELEPHONES_EXCLUS, ...CLES_REGLAGES_ENTIERS] as const;
export type CleParametreModifiable = (typeof CLES_PARAMETRES_MODIFIABLES)[number];

export function estCleParametreModifiable(cle: string): cle is CleParametreModifiable {
  return (CLES_PARAMETRES_MODIFIABLES as readonly string[]).includes(cle);
}

export function schemaReglageEntier(reglage: ReglageEntier) {
  const bornes = `Entre ${reglage.min} et ${reglage.max} (${reglage.unite})`;
  return z
    .number({ required_error: 'Valeur à saisir', invalid_type_error: 'Nombre entier attendu' })
    .int('Nombre entier attendu')
    .min(reglage.min, bornes)
    .max(reglage.max, bornes);
}

/** Libellé d'un numéro à ne jamais attribuer à un candidat : « Siège Tiba Invest », « Boutique … ». */
export const LIBELLE_NUMERO_EXCLU_MAX = 80;
export const NUMEROS_EXCLUS_MAX = 200;

/** Un numéro exclu : saisi librement, enregistré normalisé (`+216XXXXXXXX`). */
export const numeroExcluSchema = z
  .object({
    numero: z
      .string({ required_error: 'Numéro à saisir', invalid_type_error: 'Numéro à saisir' })
      .max(40, 'Numéro trop long')
      .transform((brut, ctx) => {
        const numero = normaliserTelephone(brut);
        if (!numero) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Numéro tunisien à 8 chiffres attendu' });
          return z.NEVER;
        }
        return numero;
      }),
    libelle: z
      .string({ required_error: 'Libellé à saisir', invalid_type_error: 'Libellé à saisir' })
      .trim()
      .min(1, 'Libellé à saisir')
      .max(LIBELLE_NUMERO_EXCLU_MAX, `${LIBELLE_NUMERO_EXCLU_MAX} caractères au plus`),
  })
  .strict();
export type NumeroExclu = z.output<typeof numeroExcluSchema>;

/** Valeur de `cv.telephones_exclus` : numéros normalisés, sans doublon. */
export const telephonesExclusSchema = z
  .array(numeroExcluSchema, { required_error: 'Liste de numéros attendue', invalid_type_error: 'Liste de numéros attendue' })
  .max(NUMEROS_EXCLUS_MAX, `${NUMEROS_EXCLUS_MAX} numéros au plus`)
  .superRefine((liste, ctx) => {
    const vus = new Map<string, number>();
    liste.forEach((entree, index) => {
      if (typeof entree?.numero !== 'string') return;
      const premier = vus.get(entree.numero);
      if (premier === undefined) vus.set(entree.numero, index);
      else {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'numero'],
          message: `Numéro déjà dans la liste (ligne ${premier + 1})`,
        });
      }
    });
  });

const schemasEntiers = {} as Record<CleReglageEntier, z.ZodTypeAny>;
for (const cle of CLES_REGLAGES_ENTIERS) schemasEntiers[cle] = schemaReglageEntier(REGLAGES_ENTIERS[cle]);

export const SCHEMAS_PARAMETRES: Record<CleParametreModifiable, z.ZodTypeAny> = {
  [CLE_TELEPHONES_EXCLUS]: telephonesExclusSchema,
  ...schemasEntiers,
};

export type ValidationParametre =
  | { statut: 'valide'; valeur: unknown }
  | { statut: 'cle_inconnue' }
  | { statut: 'invalide'; details: DetailValidation[] };

/**
 * Contrôle une valeur avant enregistrement. Les détails nomment le champ comme
 * dans le corps `{ valeur }` : `valeur`, `valeur.2.numero`…
 */
export function validerParametre(cle: string, valeur: unknown): ValidationParametre {
  if (!estCleParametreModifiable(cle)) return { statut: 'cle_inconnue' };
  const lu = SCHEMAS_PARAMETRES[cle].safeParse(valeur);
  if (lu.success) return { statut: 'valide', valeur: lu.data };
  return {
    statut: 'invalide',
    details: lu.error.issues.map((i) => ({ champ: ['valeur', ...i.path].join('.'), message: i.message })),
  };
}

/** PUT /parametres/:cle */
export const modificationParametreSchema = z.object({ valeur: z.unknown() }).strict();
export type ModificationParametre = z.infer<typeof modificationParametreSchema>;

/** Un paramètre tel que le rend l'API, avec sa dernière modification. */
export interface ParametreLu {
  cle: string;
  valeur: unknown;
  /** ISO */
  modifieLe: string;
  /** Nom de l'utilisateur ; null pour une valeur posée par une migration ou le semis. */
  modifiePar: string | null;
}

/** GET /parametres — indexé par clé. */
export type ParametresLus = Record<string, ParametreLu>;
